// Tour data management utilities
export interface TourData {
  lastDismissed: number;
  dismissCount: number;
  hasCompleted: boolean;
  hasSeenAutoplay: boolean; // New field to track if user has seen autoplay
}

// Constants for tour auto-start logic
export const ONE_DAY_MS = 24 * 60 * 60 * 1000; // 24 hours in milliseconds
export const MAX_AUTO_START_ATTEMPTS = 1; // Changed from 3 to 1 - only show autoplay once

// Get tour data from localStorage
export const getTourData = (tourDataKey: string): TourData => {
  const tourData = localStorage.getItem(tourDataKey);
  return tourData
    ? JSON.parse(tourData)
    : {
        dismissCount: 0,
        lastDismissed: 0,
        hasCompleted: false,
        hasSeenAutoplay: false,
      };
};

// Save tour data to localStorage
export const saveTourData = (
  tourDataKey: string,
  data: Partial<TourData>
): void => {
  const currentData = getTourData(tourDataKey);
  const newData = {
    lastDismissed: Date.now(),
    dismissCount: currentData.dismissCount,
    hasCompleted: false, // Don't mark as completed
    hasSeenAutoplay: currentData.hasSeenAutoplay, // Preserve autoplay status
    ...data,
  };
  localStorage.setItem(tourDataKey, JSON.stringify(newData));
};

// Clear tour data
export const clearTourData = (tourDataKey: string): void => {
  localStorage.removeItem(tourDataKey);
};

// Check if tour should auto-start based on dismissal history
export const shouldAutoStartTour = (tourDataKey: string): boolean => {
  try {
    const tourData = localStorage.getItem(tourDataKey);
    const currentData = tourData
      ? JSON.parse(tourData)
      : { dismissCount: 0, lastDismissed: 0, hasSeenAutoplay: false };

    const now = Date.now();
    const lastDismissed = currentData.lastDismissed || 0;
    const dismissCount = currentData.dismissCount || 0;
    const hasSeenAutoplay = currentData.hasSeenAutoplay || false;

    // Check if user has already seen autoplay for this tour
    if (hasSeenAutoplay) {
      return false;
    }

    // Check if we've exceeded max attempts
    if (dismissCount >= MAX_AUTO_START_ATTEMPTS) {
      return false;
    }

    // Check if enough time has passed since last dismissal
    const timeSinceLastDismissal = now - lastDismissed;
    return timeSinceLastDismissal >= ONE_DAY_MS;
  } catch {
    // Silently handle errors and return false
    return false;
  }
};

// Increment dismissal count for a tour
export const incrementDismissCount = (tourDataKey: string): void => {
  try {
    const currentData = getTourData(tourDataKey);
    const newData = {
      ...currentData,
      lastDismissed: Date.now(),
      dismissCount: (currentData.dismissCount || 0) + 1,
      hasSeenAutoplay: true, // Mark that user has seen autoplay
    };
    localStorage.setItem(tourDataKey, JSON.stringify(newData));
  } catch {
    // Silently handle errors
  }
};

// Mark tour as having seen autoplay (for when tour starts)
export const markTourAsSeenAutoplay = (tourDataKey: string): void => {
  try {
    const currentData = getTourData(tourDataKey);
    const newData = {
      ...currentData,
      hasSeenAutoplay: true,
    };
    localStorage.setItem(tourDataKey, JSON.stringify(newData));
  } catch {
    // Silently handle errors
  }
};

// Check if user is coming from another tour
export const checkTourNavigation = (fromTourKey: string): boolean => {
  const fromTour = localStorage.getItem(fromTourKey);
  if (fromTour === 'true') {
    localStorage.removeItem(fromTourKey);
    return true;
  }
  return false;
};

// Set flag for tour navigation
export const setTourNavigation = (toTourKey: string): void => {
  localStorage.setItem(toTourKey, 'true');
};

// Check if tour should start immediately (coming from another tour)
export const shouldStartImmediately = (tourDataKey: string): boolean => {
  // Check if coming from header tour (don't remove the flag yet)
  const fromHeaderTour = localStorage.getItem('fromHeaderTour') === 'true';
  // Check if coming from notifications tour (don't remove the flag yet)
  const fromNotificationsTour =
    localStorage.getItem('fromNotificationsTour') === 'true';

  // If coming from header tour or notifications tour, start immediately
  if (fromHeaderTour || fromNotificationsTour) {
    return true;
  }

  // Otherwise, use normal auto-start logic
  return shouldAutoStartTour(tourDataKey);
};

// Check if user has new permissions and should see header tour again
export const checkHeaderTourNewPermissions = (
  user: Record<string, unknown>
): boolean => {
  const lastPermissionCheck = localStorage.getItem('headerTourLastPermissions');
  const currentPermissions = JSON.stringify({
    canUseRealTimeNotificationsWatched:
      user?.canUseRealTimeNotificationsWatched,
    canUseRealTimeNotificationsFileOwner:
      user?.canUseRealTimeNotificationsFileOwner,
    canUseRealTimeNotificationsProperty:
      user?.canUseRealTimeNotificationsProperty,
    isProntoAdmin: user?.isProntoAdmin,
  });

  if (lastPermissionCheck !== currentPermissions) {
    localStorage.setItem('headerTourLastPermissions', currentPermissions);
    return true;
  }

  return false;
};

// Common tour actions
export const tourActions = {
  // Expand filters for tour
  expandFilters: (selector: string): void => {
    setTimeout(() => {
      const expandButton = document.querySelector(selector);
      if (expandButton && expandButton instanceof HTMLElement) {
        expandButton.click();
      }
    }, 150);
  },

  // Collapse filters for tour
  collapseFilters: (selector: string): void => {
    setTimeout(() => {
      const expandButton = document.querySelector(selector);
      if (expandButton && expandButton instanceof HTMLElement) {
        // Store current state before collapsing
        const currentState = localStorage.getItem('filtersExpanded') === 'true';
        localStorage.setItem(
          'filtersExpandedBeforeTour',
          currentState.toString()
        );

        // Always collapse filters at tour start
        if (currentState) {
          expandButton.click();
        }
      }
    }, 300);
  },

  // Navigate to another page
  navigateTo: (url: string): void => {
    window.location.href = url;
  },

  // Dispatch tour exit event
  exitTour: (): void => {
    document.dispatchEvent(new CustomEvent('tour-exit'));
  },
};

// Common tour step styles
export const tourStepStyles = {
  leftPosition: {
    position: 'left' as const,
    styles: {
      popover: (base: Record<string, unknown>) => ({
        ...base,
        left: '-300px',
        width: '600px',
        maxWidth: '600px',
      }),
    },
  },
  leftPositionWide: {
    position: 'left' as const,
    styles: {
      popover: (base: Record<string, unknown>) => ({
        ...base,
        left: '-300px',
        width: '800px',
        maxWidth: '800px',
      }),
    },
  },
  leftPositionMedium: {
    position: 'left' as const,
    styles: {
      popover: (base: Record<string, unknown>) => ({
        ...base,
        left: '-200px',
        width: '600px',
        maxWidth: '600px',
      }),
    },
  },
  leftPositionWideMedium: {
    position: 'left' as const,
    styles: {
      popover: (base: Record<string, unknown>) => ({
        ...base,
        left: '-200px',
        width: '700px',
        maxWidth: '700px',
      }),
    },
  },
  sourceFilters: {
    position: 'left' as const,
    padding: { left: 20, right: 20, top: 20, bottom: 20 },
    styles: {
      popover: (base: Record<string, unknown>) => ({
        ...base,
        left: '-200px',
        top: '50px',
        width: '600px',
        maxWidth: '600px',
      }),
    },
  },
  headerTourPosition: {
    position: 'bottom' as const,
    styles: {
      popover: (base: Record<string, unknown>) => ({
        ...base,
        marginTop: '30px', // Position tour lower on the page
        width: '600px',
        maxWidth: '600px',
      }),
    },
  },
};
